// Money is always integer euro cents. Half-unit quantities are integer halves.
// Never do arithmetic on floating-point euros.

// F1 (decision 9): every screen and export writes numbers the European way:
// dots group thousands, a comma marks decimals, "€ " before amounts
// ("€ 2.400.000", "€ 1.234,50", "6.450 people", "6,5 days"). Display only:
// what people type is read by `eurToCents` / `readNumber`, which take both styles.
// en-US is used only for its fixed separators, which are then swapped.
const numberFormats = new Map<string, Intl.NumberFormat>();
function numberFormat(min: number, max: number): Intl.NumberFormat {
  const key = `${min}-${max}`;
  let f = numberFormats.get(key);
  if (!f) {
    f = new Intl.NumberFormat("en-US", { minimumFractionDigits: min, maximumFractionDigits: max });
    numberFormats.set(key, f);
  }
  return f;
}

/** quantity (in halves) × unit cost, rounded to the cent. A missing unit cost counts as 0. */
export function lineTotalCents(quantityHalves: number, unitCostCents: number | null): number {
  if (unitCostCents === null) return 0;
  return Math.round((quantityHalves * unitCostCents) / 2);
}

export function sumCents(values: readonly number[]): number {
  let total = 0;
  for (const v of values) {
    if (!Number.isInteger(v)) throw new Error(`sumCents: ${v} is not an integer number of cents`);
    total += v;
  }
  return total;
}

/**
 * 6450 -> "6.450", 6.5 -> "6,5", 1234.567 -> "1.234,57".
 * At most `maxDecimals` (2) and at least `minDecimals` (0) decimals; `formatNumber(9, 1, 1)` -> "9,0".
 */
export function formatNumber(n: number, maxDecimals = 2, minDecimals = 0): string {
  const max = Math.max(maxDecimals, minDecimals);
  return numberFormat(minDecimals, max)
    .format(n)
    .replace(/[.,]/g, (c) => (c === "," ? "." : ","));
}

/** 9 -> "9,0", 6.5 -> "6,5": a fixed number of decimals (what `toFixed` gave, European style). */
export const formatFixed = (n: number, decimals = 1): string => formatNumber(n, decimals, decimals);

/**
 * 1234567 -> "€ 12.345,67". `cents`: "always" (the default) shows them,
 * "auto" only when the amount is not whole ("€ 38.200", "€ 38.200,50"),
 * "none" rounds to whole euros.
 */
export function formatEur(cents: number, opts: { cents?: "always" | "auto" | "none" } = {}): string {
  const mode = opts.cents ?? "always";
  const decimals = mode === "none" ? 0 : mode === "auto" && cents % 100 === 0 ? 0 : 2;
  const abs = Math.abs(cents) / 100;
  const text = formatNumber(decimals === 0 ? Math.round(abs) : abs, decimals, decimals);
  return cents < 0 && text.replace(/[0.,]/g, "") !== "" ? `-€ ${text}` : `€ ${text}`;
}

/** 1234567 -> "12345.67" (for form inputs). */
export function centsToEur(cents: number | null): string {
  if (cents === null) return "";
  const negative = cents < 0;
  const abs = Math.abs(cents);
  const whole = Math.floor(abs / 100);
  const rest = String(abs % 100).padStart(2, "0");
  return `${negative ? "-" : ""}${whole}.${rest}`;
}

/**
 * Parses a euro amount typed by a person into cents, without floating point.
 * Accepts "12345.5", "12 345,50", "12,345.50", "12.345,50", "1.234.567". Returns null
 * for empty input and NaN for anything that is not an amount.
 *
 * One separator followed by exactly three digits ("1.500", "0,125") is
 * ambiguous: it may be a thousands separator or three decimals. It is refused
 * (NaN) rather than guessed, because a wrong guess is a factor of 1 000. See
 * `eurAmountError` for the message to show.
 */
export function eurToCents(input: string): number | null {
  let s = input.replace(/[\s\u00a0\u202f\u20ac]/g, "").replace(/^EUR/i, "");
  if (s === "") return null;
  let negative = false;
  if (s.startsWith("-")) {
    negative = true;
    s = s.slice(1);
  }
  if (!/^[0-9.,]+$/.test(s)) return Number.NaN;
  const lastDot = s.lastIndexOf(".");
  const lastComma = s.lastIndexOf(",");
  const lastSep = Math.max(lastDot, lastComma);
  let whole = s;
  let fraction = "";
  if (lastSep !== -1) {
    const after = s.slice(lastSep + 1);
    const sepChar = s[lastSep];
    const sepCount = s.split(sepChar).length - 1;
    // A separator followed by 1 or 2 digits, used once, is the decimal mark.
    if (after.length <= 2 && sepCount === 1) {
      whole = s.slice(0, lastSep);
      fraction = after;
    } else if (after.length === 3 && sepCount > 1 && (lastDot === -1 || lastComma === -1)) {
      whole = s; // thousands separators only: "1.234.567"
    } else {
      // Includes one separator + 3 digits ("1.500"): thousands or decimals? Refused.
      return Number.NaN;
    }
  }
  whole = whole.replace(/[.,]/g, "");
  if (whole === "") whole = "0";
  if (!/^\d+$/.test(whole) || !/^\d{0,2}$/.test(fraction)) return Number.NaN;
  const cents = Number(whole) * 100 + Number(fraction.padEnd(2, "0"));
  if (!Number.isSafeInteger(cents)) return Number.NaN;
  return negative ? -cents : cents;
}

const AMBIGUOUS_ONE_SEPARATOR = /^-?(?:EUR|\u20ac)?\d+[.,]\d{3}$/i;

/**
 * The message for an ambiguous amount ("1.500": 1 500 or 1.5?), or null.
 * Forms show it instead of their general "Enter an amount" message.
 */
export function eurAmountError(input: string): string | null {
  const compact = input.replace(/[\s\u00a0\u202f]/g, "");
  if (!AMBIGUOUS_ONE_SEPARATOR.test(compact)) return null;
  return `"${input.trim()}" could mean thousands or decimals. Write the amount without a thousands separator, for example 1500 or 1500,00.`;
}

/** Spaces a person may type inside a number (JavaScript's \s includes no-break and narrow no-break spaces). */
const SPACES = /\s/g;

/**
 * A number typed by a person, with at most two decimals (indicator readings,
 * targets): the same reader as `eurToCents`, so "6 450" -> 6450,
 * "1.234,5" -> 1234.5 and "6,45" -> 6.45. Returns null for empty input and
 * NaN for anything else, including the ambiguous "6,450" (6450 or 6.45?) and
 * thousands groups that are not three digits ("6.4.5,1"). See
 * `numberInputError` for the message to show.
 */
export function readNumber(input: string): number | null {
  const compact = input.replace(SPACES, "");
  if (compact === "") return null;
  if (!/^-?\d+(?:[.,]\d+)*$/.test(compact)) return Number.NaN;
  const cents = eurToCents(compact);
  if (cents === null || Number.isNaN(cents)) return Number.NaN;
  // Whatever is left of the decimal mark is groups of three after the first.
  const lastSep = Math.max(compact.lastIndexOf("."), compact.lastIndexOf(","));
  const decimals = lastSep !== -1 && compact.length - lastSep - 1 <= 2 && compact.split(compact[lastSep]).length === 2;
  const whole = (decimals ? compact.slice(0, lastSep) : compact).replace(/^-/, "");
  if (!/^(?:\d+|\d{1,3}(?:\.\d{3})+|\d{1,3}(?:,\d{3})+)$/.test(whole)) return Number.NaN;
  return cents / 100;
}

/** The message for a number `readNumber` refuses, or null when it reads fine (or is empty). */
export function numberInputError(input: string): string | null {
  const n = readNumber(input);
  if (n === null || !Number.isNaN(n)) return null;
  const compact = input.replace(SPACES, "");
  const m = /^(-?)(\d+)([.,])(\d{3})$/.exec(compact);
  if (m) {
    const [, sign, head, sep, tail] = m;
    const asDecimal = `${sign}${Number(`${head}.${tail}`)}`;
    return `"${input.trim()}" could mean ${sign}${head}${tail} or ${asDecimal}. Write it without a thousands separator, for example ${sign}${head}${tail}, or with a decimal ${sep === "," ? "comma" : "point"}: ${asDecimal.replace(".", sep)}.`;
  }
  // S-LF9: "Enter a number…" so the error summary names the field ("Value: Enter a number…").
  return "Enter a number, with at most two decimals.";
}

/** A reading ten times above or below a (non-zero) baseline is probably a typing slip: say so before saving. */
export function farFromBaseline(value: number, baseline: number | null): boolean {
  if (baseline === null || baseline === 0 || value === 0) return false;
  const ratio = Math.abs(value / baseline);
  return ratio >= 10 || ratio <= 0.1;
}

/** 2.5 -> 5 halves. Throws for values that are not a multiple of 0.5. */
export function toHalves(value: number): number {
  const halves = value * 2;
  if (!Number.isInteger(halves)) throw new Error(`${value} is not a multiple of 0.5`);
  return halves;
}

/** 5 halves -> 2.5 */
export function fromHalves(halves: number): number {
  return halves / 2;
}

/** 5 -> "2,5", 4 -> "2" (",5" only when needed). */
export function formatHalves(halves: number): string {
  return halves % 2 === 0 ? formatNumber(halves / 2, 0) : formatFixed(halves / 2, 1);
}
